<template>
    <AdminPageInner v-if="sessionGroup.id" :breadcrumbs="breadcrumbs">
        <template #heading>
            Edit:
            <CalendarFormat :date="startDate" :showState="false"/>
            -
            <CalendarFormat :date="endDate" :showState="false"/>
        </template>
        <SessionForm :course="course" :sessionGroup="sessionGroup" @submit="onSubmit">
            <template #footer>
                <div class="flex flex-row-reverse items-center">
                    <SubmitButton
                        :submitting="form.processing"
                        :disabled="form.errors.any()">
                        Update
                    </SubmitButton>
                    <router-link :to="redirectRoute" class="text-gray-500 mr-4">
                        Cancel
                    </router-link>
                </div>
            </template>
        </SessionForm>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import AdminPageInner from "@/Components/Layout/AdminPageInner";
import SessionForm from "@/Pages/Admin/CourseSessionGroups/Components/Form";
import SubmitButton from "@/Components/Form/SubmitButton";
import CalendarFormat from "@/Components/CalendarFormat";
import {sortBy} from "lodash";
import dayjs from "dayjs";

export default {

    name: "CourseSessionEditPage",

    data() {
        return {
            course: {},
            sessionGroup: {},
            form: this.$form(),
        }
    },

    provide() {
        return {
            form: this.form
        }
    },

    components: {
        AdminPageInner,
        CalendarFormat,
        SessionForm,
        SubmitButton
    },

    methods: {
        setData({course, sessionGroup}) {

            let formData = {
                spaces: sessionGroup.spaces,
                course_sessions: sessionGroup.sessions.map(session => ({
                    id: session.id,
                    module_title: session.module.title,
                    type: session.type,
                    location_url: session.location_url,
                    location_notes: session.location_notes,
                    start: session.start,
                    end: session.end,
                    disabled: !session.user_can.update
                }))
            }

            this.form.withData(formData)
            this.course = course;
            this.sessionGroup = sessionGroup;
        },
        onSubmit() {

            if (this.form.processing) {
                return;
            }

            this.form.put(route('api.course-session-groups.update', this.sessionGroup))
                .then(async () => {
                    this.$notify.success('Session updated');
                    await this.$router.push(this.redirectRoute);
                })
                .catch(console.error);
        },
    },

    computed: {
        startDate() {
            const sessions = sortBy(this.sessionGroup.sessions, session => dayjs(session.start));

            return sessions[0].start;
        },
        endDate() {
            const sessions = sortBy(this.sessionGroup.sessions, session => dayjs(session.end)).reverse();

            return sessions[0].end;
        },
        breadcrumbs() {
            return [
                {
                    label: 'Admin', to: {name: 'admin'}
                },
                {
                    label: 'Courses', to: {name: 'admin.courses.index'}
                },
                {
                    label: `${this.course.title || 'Course'}`,
                    to: {name: 'admin.courses.show', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Session Groups`,
                    to: {name: 'admin.courses.session-groups.index', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Group`,
                    to: {
                        name: 'admin.courses.session-groups.index',
                        params: {
                            courseId: this.$route.params.courseId,
                            courseSessionGroupId: this.$route.params.courseSessionGroupId
                        }
                    }
                },
                {
                    label: `Edit`, to: {name: this.$route.name, params: this.$route.params}
                },
            ];
        },
        redirectRoute() {
            return this.$route.meta.previous
                ? this.$route.meta.previous
                : {
                    name: 'admin.courses.session-groups.show',
                    params: {courseSessionGroupId: this.$route.params.courseSessionGroupId}
                };
        }
    },

    async beforeRouteEnter(to, from, next) {

        const data = await fetchAllData(to);
        next(vm => vm.setData(data));
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        axios.get(route('api.course-session-groups.edit', to.params.courseSessionGroupId), {params: to.query}),
        axios.get(route('api.courses.show', to.params.courseId), {params: to.query}),
    ])

    return {
        sessionGroup: data[0].data,
        course: data[1].data
    };
}
</script>
